import { useEffect, useRef, useState } from 'react';
import { Button, Pill } from '../primitives';
import { OrgCreateForm } from '../primitives';
import type { PendingInvitation } from '../../lib/vm/useOrgSwitcherVM';
import type { ShellMembership } from './useShellSession';

export interface IdentityFlyoutProps {
  activeOrgId: string | null;
  memberships: ReadonlyArray<ShellMembership>;
  onPickOrg: (m: ShellMembership) => void;
  onAccount: () => void;
  onSignOut: () => void;
  onClose: () => void;
  /** 17.3b.2 — Pending invitations addressed to the viewer's email,
   *  rendered as a dedicated section between Switch-to and Account. The VM
   *  owns load/accept/decline; the flyout is a pure view. */
  pendingInvitations?: ReadonlyArray<PendingInvitation>;
  onAcceptInvitation?: (token: string) => void;
  onDeclineInvitation?: (invitationId: string) => void;
  /** Browser-reported connectivity — surfaced as a flyout row. */
  online?: boolean;
  /** Opens the sync-diagnostics modal (host shell). */
  onDiagnosticsClick?: () => void;
  /** create-additional-orgs — handler wired by SignedInShell. When provided,
   *  a "+ Create new org" row and inline form are shown after the Switch-to
   *  section. */
  onCreateOrg?: (name: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Disables the create affordance while a create or switch is in progress. */
  createOrgDisabled?: boolean;
}

export function IdentityFlyout({
  activeOrgId,
  memberships,
  onPickOrg,
  onAccount,
  onSignOut,
  onClose,
  pendingInvitations,
  onAcceptInvitation,
  onDeclineInvitation,
  online = true,
  onDiagnosticsClick,
  onCreateOrg,
  createOrgDisabled = false
}: IdentityFlyoutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onPointer(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target || !ref.current) return;
      // Click on the trigger itself is handled there; this is for clicks
      // outside the flyout body.
      if (!ref.current.contains(target)) {
        const trigger = (target as HTMLElement).closest?.('[data-flyout-trigger]');
        if (trigger) return;
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [onClose]);

  const others = memberships.filter((m) => m.org_id !== activeOrgId);
  const active = memberships.find((m) => m.org_id === activeOrgId) ?? null;
  const invitations = pendingInvitations ?? [];
  const connectivityLabel = online ? 'Connected' : 'No network';

  return (
    <div
      ref={ref}
      className="pn-flyout"
      role="menu"
      aria-label="Identity menu"
      data-testid="identity-flyout"
    >
      {active && (
        <div className="pn-flyout-section">
          <button
            type="button"
            className="pn-flyout-row"
            role="menuitem"
            aria-current="true"
            data-testid="flyout-active-org"
          >
            <span className="pn-flyout-row-main">
              <span className="pn-flyout-row-name">{active.org_name}</span>
              {active.function_role_name && (
                <span className="pn-flyout-row-role">{active.function_role_name}</span>
              )}
            </span>
            <span className="pn-flyout-row-check" aria-label="Active org">✓</span>
          </button>
        </div>
      )}

      {others.length > 0 && (
        <div className="pn-flyout-section" data-testid="flyout-switch-section">
          <div className="pn-flyout-eyebrow">Switch to</div>
          {others.map((m) => (
            <button
              key={m.org_id}
              type="button"
              className="pn-flyout-row"
              role="menuitem"
              onClick={() => onPickOrg(m)}
              data-testid="flyout-switch-org"
              data-suspended={m.suspended_at ? 'true' : 'false'}
            >
              <span className="pn-flyout-row-main">
                <span className="pn-flyout-row-name">{m.org_name}</span>
                {m.function_role_name && (
                  <span className="pn-flyout-row-role">{m.function_role_name}</span>
                )}
              </span>
              {m.suspended_at ? (
                <Pill
                  status="suspended"
                  data-testid="flyout-switch-org-suspended"
                >
                  Suspended
                </Pill>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {onCreateOrg && (
        <div className="pn-flyout-section" data-testid="flyout-create-org-section">
          {!createOpen ? (
            <button
              type="button"
              className="pn-flyout-row"
              role="menuitem"
              onClick={() => setCreateOpen(true)}
              disabled={createOrgDisabled}
              data-testid="flyout-create-org-open"
            >
              <span>+ Create new org</span>
            </button>
          ) : (
            <OrgCreateForm
              variant="flyout"
              disabled={createOrgDisabled}
              onCancel={() => setCreateOpen(false)}
              onSubmit={async (name) => {
                const r = await onCreateOrg(name);
                if (r.ok) setCreateOpen(false);
                return r;
              }}
              testIdPrefix="flyout-create-org-form"
            />
          )}
        </div>
      )}

      {invitations.length > 0 && (
        <div
          className="pn-flyout-section pn-flyout-invitations"
          data-testid="flyout-pending-invitations"
        >
          <div className="pn-flyout-eyebrow">Pending invitations</div>
          <ul className="pn-flyout-invitations-list">
            {invitations.map((inv) => (
              <li
                key={inv.id}
                className="pn-flyout-invitation-row"
                data-testid="flyout-pending-invitation"
              >
                <div className="pn-flyout-invitation-meta">
                  <span className="pn-flyout-invitation-name">{inv.org_name}</span>
                  <span className="pn-flyout-invitation-eyebrow">
                    Invited by {inv.inviter_name ?? 'someone on the team'}
                  </span>
                </div>
                <div className="pn-flyout-invitation-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="flyout-pending-invitation-decline"
                    onClick={() => onDeclineInvitation?.(inv.id)}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="flyout-pending-invitation-accept"
                    onClick={() => onAcceptInvitation?.(inv.token)}
                  >
                    Accept
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pn-flyout-section" data-testid="flyout-connectivity-section">
        <button
          type="button"
          className="pn-flyout-row"
          role="menuitem"
          onClick={() => {
            onDiagnosticsClick?.();
            onClose();
          }}
          data-testid="connectivity-indicator"
          data-online={online ? 'true' : 'false'}
          aria-label={`${connectivityLabel} — open sync diagnostics`}
        >
          <span
            className={[
              'pn-flyout-connectivity',
              online ? '' : 'pn-flyout-connectivity-offline'
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="pn-conn-dot" aria-hidden="true" />
            <span>{connectivityLabel}</span>
          </span>
        </button>
      </div>

      <div className="pn-flyout-section">
        <button
          type="button"
          className="pn-flyout-row"
          role="menuitem"
          onClick={onAccount}
          data-testid="flyout-account"
        >
          <span>Account</span>
        </button>
        <button
          type="button"
          className="pn-flyout-row"
          role="menuitem"
          onClick={onSignOut}
          data-testid="flyout-signout"
        >
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}
